import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  buildAssets,
  checkDomain,
  looksLikeTypoOf,
  countLiveAssets,
  countLiveEmbeds,
  expandHosts,
  assetsForSite,
  isNotShipped,
  isOpenMode,
  isPublished,
  KIND_WORD,
  liveEmbedsOnSite,
  mintLocalKey,
  placementAt,
  placementLabel,
  siteSummary,
  sitesForAsset,
  snippetFor,
  snippetTokens,
  EMBED_PLATFORMS,
  togglePlacement,
  countOffEmbeds,
  bulkOutcome,
  assetSiteBadge,
  type EmbedSite,
  type OpenMode,
  type Placements,
  type SaveState,
} from "rc-app/utils/publishEmbedFlow";
import {
  createEmbedKey,
  deleteEmbedSite,
  listEmbedKeys,
  updateEmbedKey,
  updateEmbedSettings,
} from "rc-app/api/embedActionsApi";
import "./PublishEmbedModal.css";

/* ── Publish on your website ─────────────────────────────────────────────
   Spec:   docs/product specs/WEBSITE_EMBED_SPEC.md (v6)
   Design: docs/product specs/design_handoff_publish_modal/ (v4 = design of record)

   Two views in one modal, per the design:
     A — one asset, ticking the websites it appears on, one code per website
     B — project-wide: the websites × assets matrix + the website registry

   ── FOUR CORRECTIONS TO THE DESIGN BUNDLE, DELIBERATE (spec §10.1.2/§10.1.3) ──
   1. Host is embed.openskyvr.com, never openskyvr.com — the main app must stay
      unframeable, which is the whole reason for a separate host.
   2. The key is ONE opaque `emb_…` per (asset, site). The bundle's composite
      `{asset}-{site}` is walkable: hold one code, construct a sibling's.
      `MM1-S1` survives as the UI label only.
   3. `allow` carries the motion permissions too, on both iframes.
   4. Footer is "Turn off all embeds" (reversible), not "Revoke".
   Plus, on founder's call: NO edit-website dialog, and NO delete button on the
   asset view — delete lives only in project-wide settings, because on an asset
   row it reads as "remove this placement" while actually deleting the website
   for the whole project.

   ── THE ONE RULE THAT MUST NOT BE BROKEN ──
   Unticking sets `published: false` and KEEPS the key. If it destroyed the key,
   re-ticking would mint a new one and the snippet already pasted on the
   customer's live page would be dead forever, from one mis-click.
*/

/** A placement left behind by a deleted website — revivable by re-adding the domain. */
interface Orphan {
  key: string;
  assetId: string;
  label: string;
  /** The website it WAS on. Without it two leftovers read identically and neither says why. */
  removedHost: string;
}

/* ONE SWITCH, AND IT SAYS "FULLSCREEN".
   This began as two option cards worded by what the visitor experiences —
   "Takes over the screen" / "Plays in the page" — on the reasoning that
   "fullscreen / not fullscreen" makes one option sound like a missing feature.
   Wrong twice over. The founder, who had specified the setting a day earlier,
   looked at it and asked "is that the fullscreen thing?": nobody looking for
   this control is looking for a description of it. And a pair of equal-weight
   cards asks the customer to weigh two options when we have a view on which is
   better — so it should say so, and still let them choose.

   Hence a plain on/off switch with a recommendation underneath. The default is
   on, which is also what we'd advise, so most customers never touch it. */
const isFs = (m?: OpenMode): boolean => (m || "fullscreen") === "fullscreen";

export interface PublishEmbedModalProps {
  projectId: string;
  projectName: string;
  /** Meta models — `sub` is an optional secondary line (tower, config…). */
  metaModels?: Array<{ id: string; name?: string; sub?: string }>;
  /** Digital twins. Route views are out of scope — they have no lead gate. */
  digitalTwins?: Array<{ id: string; name?: string; sub?: string }>;
  onClose: () => void;
  /** Preview harness only: seed sites/placements without a backend. */
  seed?: { sites: EmbedSite[]; placements: Placements };
}

/* ── Icons (inline SVG, 24×24, stroked — the design's own set) ────────── */
const Tick = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const Globe = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.6 2.5 15.4 0 18M12 3c-2.5 2.6-2.5 15.4 0 18" />
  </svg>
);
const Close = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
const Chevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);
const CopyIco = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="12" height="12" rx="2.5" />
    <path d="M15 5H6a2 2 0 0 0-2 2v9" />
  </svg>
);
const Trash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 7h16M9.5 7V5.2a1.2 1.2 0 0 1 1.2-1.2h2.6a1.2 1.2 0 0 1 1.2 1.2V7M6.5 7l.8 12.1A1.9 1.9 0 0 0 9.2 21h5.6a1.9 1.9 0 0 0 1.9-1.9L17.5 7M10.5 11v6M13.5 11v6" />
  </svg>
);
const Info = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8v.5" />
  </svg>
);
const MMIco = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9.5 12 4l9 5.5v9L12 24l-9-5.5Z" transform="scale(.92) translate(1 -1)" />
    <path d="M3 9.5 12 15l9-5.5M12 15v8" />
  </svg>
);
const DTIco = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v18M4 7.5v9L12 21l8-4.5v-9L12 3Z" />
    <path d="M4 7.5 12 12l8-4.5" />
  </svg>
);
const TableIco = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="4" width="17" height="16" rx="2" />
    <path d="M3.5 9h17M9 9v11M14.5 9v11" />
  </svg>
);

/* ── Small pieces ────────────────────────────────────────────────────── */

const HostList: React.FC<{ domain: string; subs: boolean }> = ({ domain, subs }) => (
  <>
    {expandHosts(domain, subs).map((h) => (
      <div key={h.host} className={"pem-host" + (h.included ? "" : " pem-dim")}>
        <i><Tick /></i>
        {h.host}
        <em>{h.note}</em>
      </div>
    ))}
  </>
);

/**
 * The embed code, collapsed by default, with a tab per platform.
 *
 * COLLAPSED, because the card is no longer a single blob of markup: it now
 * carries where-to-paste instructions, and left open it buries the website list
 * it belongs to. Copy stays in the header, so the common action — grab the code
 * and send it on — is ONE click, fewer than before, and the instructions are
 * there for whoever actually pastes it.
 *
 * TABS, because the modal previously handed over markup and said nothing about
 * where it goes. Every customer is on a different stack and the web team's
 * first two questions are always "where does this paste" and "is my copy the
 * same as everyone else's". The second is answered on every tab even when the
 * answer is a boring yes — silence reads as "maybe there IS a different one",
 * and a team that suspects that stops and emails instead of shipping.
 */
const CodeCard: React.FC<{
  label: string;
  domain: string;
  keyStr: string;
  assetName: string;
  projectName: string;
}> = ({ label, domain, keyStr, assetName, projectName }) => {
  const [open, setOpen] = useState(false);
  const [platId, setPlatId] = useState(EMBED_PLATFORMS[0].id);
  const [copied, setCopied] = useState(false);
  const timer = useRef<any>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const plat = EMBED_PLATFORMS.find((p) => p.id === platId) || EMBED_PLATFORMS[0];
  const input = { key: keyStr, assetName, projectName };

  const copy = () => {
    try {
      navigator.clipboard?.writeText(snippetFor(input, plat.flavor));
    } catch {
      /* clipboard blocked — the code is selectable in the <pre> either way */
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1800);
  };

  const copyBtn = (withFlavour: boolean) => (
    <button type="button" className="pem-copy" onClick={copy}>
      {copied ? <Tick /> : <CopyIco />}
      {copied
        ? "Copied"
        : withFlavour && plat.flavor === "react"
          ? "Copy React code"
          : "Copy"}
    </button>
  );

  return (
    <div className={"pem-code" + (open ? " pem-open" : "")}>
      <div className="pem-code-h">
        <button
          type="button"
          className="pem-code-t"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span className="pem-chev2"><Chevron /></span>
          <b>Embed code</b>
          <em>{label} · {domain}</em>
        </button>
        {copyBtn(true)}
      </div>

      {open && (
        <div className="pem-code-b">
          <p className="pem-code-q">Where does this go?</p>
          <div className="pem-tabs" role="tablist" aria-label="Website platform">
            {EMBED_PLATFORMS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={p.id === platId}
                className={"pem-tab" + (p.id === platId ? " pem-on" : "")}
                onClick={() => setPlatId(p.id)}
              >
                {p.label}
                {!p.sameCode && <i className="pem-tab-dot" aria-hidden="true" />}
              </button>
            ))}
          </div>

          <p className="pem-code-lede">{plat.lede}</p>
          <p className={"pem-same" + (plat.sameCode ? "" : " pem-same--diff")}>
            {plat.sameCode
              ? "Same code on every platform here — React is the only one that differs. Paste it exactly as it is; nothing in it needs editing."
              : "Different code. The version below replaces the HTML one — paste this, not both."}
          </p>

          <ol className="pem-steps">
            {plat.steps.map((s) => <li key={s}>{s}</li>)}
          </ol>

          {plat.caution && (
            <p className="pem-caution"><Info /><span>{plat.caution}</span></p>
          )}

          <div className="pem-codecard">
            <div className="pem-codebar">
              <span>
                {plat.flavor === "react"
                  ? "ImmersiveWalkthrough.jsx"
                  : `${label} · ${domain}`}
              </span>
              {copyBtn(false)}
            </div>
            <pre>
              {/* Tokenised rather than innerHTML: the asset and project names are
                  user-entered and must never be interpolated into markup. */}
              {snippetTokens(input, plat.flavor).map((t, i) => (
                <span key={i} className={t.k === "plain" ? undefined : `pem-${t.k[0]}`}>{t.s}</span>
              ))}
            </pre>
          </div>

          <p className="pem-code-foot">
            This code is for <b>{domain}</b> only — it renders nowhere else, so another
            website needs its own code from this modal.
          </p>
        </div>
      )}
    </div>
  );
};

/* ── The modal ───────────────────────────────────────────────────────── */

const PublishEmbedModal: React.FC<PublishEmbedModalProps> = ({
  projectId,
  projectName,
  metaModels,
  digitalTwins,
  onClose,
  seed,
}) => {
  const assets = useMemo(
    () => buildAssets(metaModels || [], digitalTwins || []),
    [metaModels, digitalTwins],
  );

  const [sites, setSites] = useState<EmbedSite[]>(seed?.sites || []);
  const [placements, setPlacements] = useState<Placements>(seed?.placements || {});
  /** 'proj' = View B, otherwise an asset id. */
  const [cur, setCur] = useState<string>(assets[0]?.id || "proj");
  /** null = default to the first published row; 'none' = all collapsed. */
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [save, setSave] = useState<SaveState>("idle");
  const [saveMsg, setSaveMsg] = useState("");
  const [draft, setDraft] = useState("");
  /* Have the project's websites and placements ARRIVED yet?
     Without this the header renders its counts from empty state and states
     "0 websites · 0 embeds in use" as fact for as long as the fetch takes — seven seconds
     on a slow morning. A zero is a claim, and that one reads as "this project publishes
     nothing", which is the opposite of what an admin opening this screen usually knows.
     Seeded previews are loaded by definition. */
  const [loaded, setLoaded] = useState<boolean>(!!seed);

  /* A website switched ON but with nothing ticked yet. Local only — see toggleSiteEmbeds. */
  const [siteEnabled, setSiteEnabled] = useState<Record<string, boolean>>({});
  /**
   * Does this server know about `open_mode` yet?
   *
   * A CAPABILITY PROBE, not a feature flag: we look for the property on the keys
   * the server just sent back. `PatchKeyRequest` drops fields it doesn't declare
   * and then 400s "Nothing to update." when nothing it DOES know about was sent —
   * so sending `open_mode` to a server without the field is a guaranteed error,
   * and the optimistic state gets reverted the instant it is applied. Which is
   * exactly what a user sees as "the toggle doesn't work".
   *
   * Probing rather than hard-coding means the switch comes alive on its own the
   * day the field ships, with no second frontend release to coordinate.
   * (EMBED_BACKEND_HANDOFF_5.md §2.)
   */
  const [supportsOpenMode, setSupportsOpenMode] = useState<boolean>(!!seed);
  const [draftErr, setDraftErr] = useState("");
  /** Placements whose website was deleted: dead to visitors, invisible here until now. */
  const [orphans, setOrphans] = useState<Orphan[]>([]);

  /* Escape closes — matches every other modal in the app. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* Lock the page behind the modal. Without this, scrolling over the scrim
     moves the project page underneath, which reads as the modal drifting. */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  /* Load the project's keys + site registry (two server calls, joined in the
     API client). If the server can't be reached at all we keep whatever seed we
     have and say so in the footer, rather than showing an empty modal that
     looks like data loss. Any answer the server DOES give is shown as-is. */
  useEffect(() => {
    if (seed || !projectId) return undefined;
    let cancelled = false;
    listEmbedKeys(projectId)
      .then((res: any) => {
        if (cancelled) return;
        const domains = res?.embed_settings?.allowed_domains || [];
        setSites(
          domains.map((d: any, i: number) => ({
            id: String(d.id || d.host),
            code: `S${i + 1}`,
            domain: String(d.host || ""),
            label: String(d.label || ""),
            lead: d.lead_capture !== false,
            subs: !!d.include_subdomains,
            // null when nobody has chosen — kept as null, not coerced, so siteEmbedsOn can
            // still infer from the ticks for every project that predates the switch.
            embedsEnabled: d.embeds_enabled ?? null,
          })),
        );
        // `hasOwnProperty`, not truthiness: the field is meant to be ABSENT until
        // someone sets a mode, so `open_mode: null` is a server that HAS it.
        setSupportsOpenMode((res?.embed_keys || []).some(
          (k: any) => k && Object.prototype.hasOwnProperty.call(k, "open_mode"),
        ));
        const next: Placements = {};
        const stranded: Orphan[] = [];
        const liveSiteIds = new Set(domains.map((d: any) => String(d.id || d.host)));
        (res?.embed_keys || []).forEach((k: any) => {
          const siteId = String(k?.site_id || "");
          const assetId = String(k?.experience_id || "");
          if (!siteId || !assetId || !k?.key) return;
          // A placement whose website was deleted has no row to live in — the view is built
          // per registered site. It used to vanish from this screen entirely while still
          // sitting in the database, so nobody could see it or revoke it. Collect it.
          if (k.state === "site_removed" || !liveSiteIds.has(siteId)) {
            stranded.push({
              key: String(k.key), assetId, label: String(k.label || ""),
              // site_host is null for these by design — it means "where this renders", and a
              // removed site renders nowhere. removed_host names it anyway.
              removedHost: String(k.removed_host || ""),
            });
            return;
          }
          next[siteId] = next[siteId] || {};
          next[siteId][assetId] = {
            key: String(k.key),
            published: k.published !== false,
            // Left undefined when the server has no opinion, which is also the state
            // before the field exists at all — `resolveOpenMode` reads that as "default".
            ...(isOpenMode(k.open_mode) ? { openMode: k.open_mode } : {}),
          };
        });
        setPlacements(next);
        setOrphans(stranded);
        setLoaded(true);
      })
      .catch((err: any) => {
        if (cancelled) return;
        // Loaded means "we asked and this is the answer", not "it worked" — the save state
        // below carries the failure. Leaving it false would hide the counts for good.
        setLoaded(true);
        if (isNotShipped(err)) {
          setSave("unsaved");
          setSaveMsg("Not saved — couldn’t reach the server");
        } else {
          setSave("error");
          setSaveMsg("Couldn’t load your websites");
        }
      });
    return () => { cancelled = true; };
  }, [projectId, seed]);

  /**
   * Every control is its own optimistic mutation — the footer promises
   * continuous saving and there is no Save button.
   *
   * On a REAL failure we revert and say so: otherwise the ABA sees a gate
   * switched off while the customer's live site still gates, and the modal is
   * confidently wrong about a production surface. On "not shipped" we KEEP the
   * optimistic state, because reverting every toggle would make the screen
   * untestable until the backend lands.
   */
  const commit = useCallback(
    async (apply: () => void, revert: () => void, call: () => Promise<any>, what: string) => {
      apply();
      if (seed) return; // preview harness: local only, never calls out
      setSave("saving");
      setSaveMsg("");
      /* A request that never settles used to leave "Saving…" on screen for ever, over an
         optimistic change that may never have landed — the screen looking busy rather than
         wrong, which is the more dangerous of the two. Say so after a while. The change is
         NOT reverted: it may still be in flight, and undoing a save that then succeeds is
         worse than admitting we don't know. */
      let slow: any = setTimeout(() => {
        setSave("unsaved");
        setSaveMsg("Still saving — check your connection, then reload to see what stuck");
      }, 15000);
      const done = () => { if (slow) { clearTimeout(slow); slow = null; } };
      try {
        await call();
        done();
        setSave("idle");
      } catch (err: any) {
        done();
        if (isNotShipped(err)) {
          setSave("unsaved");
          setSaveMsg("Not saved — couldn’t reach the server");
        } else {
          revert();
          setSave("error");
          setSaveMsg(`Couldn’t save ${what} — reverted`);
        }
      }
    },
    [seed],
  );

  /** Serialise the registry the way the project-level PATCH expects it. */
  const domainsPayload = useCallback(
    (list: EmbedSite[]) => ({
      allowed_domains: list.map((s) => ({
        id: s.id,
        host: s.domain,
        include_subdomains: s.subs,
        lead_capture: s.lead,
        label: s.label || undefined,
        embeds_enabled: s.embedsEnabled,
      })),
    }),
    [],
  );

  /* ── Mutations ─────────────────────────────────────────────────────── */

  const togglePublish = (siteId: string, assetId: string) => {
    const before = placements;
    const { next, key, published } = togglePlacement(placements, siteId, assetId);
    const existed = !!placementAt(before, siteId, assetId);
    const asset = assets.find((a) => a.id === assetId);
    const site = sites.find((s) => s.id === siteId);
    setOpenRow(published ? siteId : null);
    commit(
      () => setPlacements(next),
      () => setPlacements(before),
      async () => {
        if (existed) {
          await updateEmbedKey(key, { published });
          return;
        }
        // First placement on this site: mint the key, then adopt the server's.
        const res: any = await createEmbedKey({
          project_id: projectId,
          experience_kind: asset?.kind === "DT" ? "digital_twin" : "meta_model",
          experience_id: assetId,
          site_id: siteId,
          label: asset && site ? placementLabel(asset, site) : undefined,
          // No lead_capture — it lives on the SITE now (§11.2), so a new
          // placement inherits its site's gate automatically.
          published: true,
        });
        if (!res?.key) return;
        // The mint is idempotent, and its reuse path returns the EXISTING row
        // without applying the `published` we asked for. So a key that was
        // turned off earlier comes back still off — with a 200 and a valid key,
        // which the modal would happily show as live. That is the worst failure
        // this screen has: a builder pastes the snippet, their web team ships
        // it, and the page renders a grey box while this says it is on.
        // Publishing explicitly costs one request and cannot silently no-op.
        if (res.reused && res.published !== true) {
          await updateEmbedKey(String(res.key), { published: true });
        }
        setPlacements((p) => ({
          ...p,
          [siteId]: {
            ...(p[siteId] || {}),
            [assetId]: { key: String(res.key), published: true },
          },
        }));
      },
      "that embed",
    );
  };

  /**
   * What the visitor's FIRST tap does on this one placement.
   *
   * Per placement rather than per website, unlike the lead gate. The gate is
   * per-site because a visitor should be asked once per site — a rule about the
   * person. This is a rule about the page, and one site routinely has both: a
   * hero walkthrough at the top of a landing page, and a card further down.
   *
   * IT DOES NOT TOUCH THE SNIPPET. The embed page reads this from the render
   * payload, so flipping it changes a live page with nothing asked of the
   * customer's web team — the same guarantee publishing and the lead gate give.
   * An earlier version wrote `?fs=0` into the src so the setting would work
   * before the backend field shipped; that made the modal show one code while
   * the customer's page ran another, which is a worse failure than the setting
   * not existing yet.
   *
   * So until `open_mode` lands on EmbedKey (EMBED_BACKEND_HANDOFF_5.md §2) this
   * switch persists in the UI and the PATCH is ignored server-side.
   */
  const setOpenMode = (siteId: string, assetId: string, mode: OpenMode) => {
    // Guarded as well as disabled: a PATCH to a server without the field is a
    // certain 400, and `commit` reverts on a real error — so the switch would
    // flip and snap straight back, which is worse than not moving at all.
    if (!supportsOpenMode) return;
    const pl = placementAt(placements, siteId, assetId);
    if (!pl || pl.openMode === mode) return;
    const before = placements;
    commit(
      () => setPlacements((p) => ({
        ...p,
        [siteId]: { ...(p[siteId] || {}), [assetId]: { ...pl, openMode: mode } },
      })),
      () => setPlacements(before),
      () => updateEmbedKey(pl.key, { open_mode: mode }),
      "how this embed opens",
    );
  };

  const toggleLead = (siteId: string) => {
    const before = sites;
    const next = sites.map((s) => (s.id === siteId ? { ...s, lead: !s.lead } : s));
    commit(
      () => setSites(next),
      () => setSites(before),
      () => updateEmbedSettings(projectId, domainsPayload(next)),
      "the lead gate",
    );
  };

  const toggleSubs = (siteId: string) => {
    const before = sites;
    const next = sites.map((s) => (s.id === siteId ? { ...s, subs: !s.subs } : s));
    commit(
      () => setSites(next),
      () => setSites(before),
      () => updateEmbedSettings(projectId, domainsPayload(next)),
      "the subdomain setting",
    );
  };

  /**
   * Add a website. `publishFor` publishes the current asset there in one step —
   * the design's "Add & publish here".
   */
  const addSite = (raw: string, publishFor?: string) => {
    const res = checkDomain(raw, { existing: sites.map((s) => s.domain) });
    if (!res.ok) { setDraftErr(res.error || "That doesn’t look like a domain."); return; }
    /* A typo is syntactically valid, so it cannot be REFUSED — we don't know how they spell
       their own domain. But `opesnkyvr.com` is live on stage right now: two letters
       transposed, key minted, embed silently never worked, and nothing said a word. Ask once,
       and let them proceed. */
    const near = looksLikeTypoOf(res.host, sites.map((s) => s.domain));
    if (near && !window.confirm(
      `Add ${res.host}?\n\n` +
      `It is one letter away from ${near}, which is already on this project. A mistyped ` +
      `domain looks fine here and then shows nothing on the real website, with no error.\n\n` +
      `Add it anyway if that is genuinely a different site.`,
    )) { setDraftErr(""); return; }
    setDraftErr("");
    setDraft("");
    const site: EmbedSite = {
      id: `local-${res.host}`,
      code: `S${sites.length + 1}`,
      domain: res.host,
      label: "",
      lead: true, // gate Open by default — "do I want leads from my website?"
      subs: false, // wildcards are opt-in, never implied
    };
    const nextSites = [...sites, site];
    const before = sites;
    const beforeP = placements;
    let nextP = placements;
    if (publishFor) {
      nextP = {
        ...placements,
        [site.id]: { [publishFor]: { key: mintLocalKey(), published: true, local: true } },
      };
    }
    commit(
      () => { setSites(nextSites); if (publishFor) { setPlacements(nextP); setOpenRow(site.id); } },
      () => { setSites(before); setPlacements(beforeP); },
      async () => {
        const saved: any = await updateEmbedSettings(projectId, domainsPayload(nextSites));
        // Adopt the server's id for the new entry, so later PATCHes address it.
        const match = (saved?.allowed_domains || []).find((d: any) => d?.host === res.host);
        if (match?.id && match.id !== site.id) {
          setSites((list) => list.map((s) => (s.id === site.id ? { ...s, id: String(match.id) } : s)));
          setPlacements((p) => {
            if (!p[site.id]) return p;
            const moved = { ...p, [String(match.id)]: p[site.id] };
            delete moved[site.id];
            return moved;
          });
        }
        if (publishFor) {
          const asset = assets.find((a) => a.id === publishFor);
          const savedId = String(match?.id || site.id);
          const res2: any = await createEmbedKey({
            project_id: projectId,
            experience_kind: asset?.kind === "DT" ? "digital_twin" : "meta_model",
            experience_id: publishFor,
            site_id: savedId,
            label: asset ? placementLabel(asset, { ...site, id: savedId }) : undefined,
            published: true,
          });
          // Adopt the server's key, exactly as ticking an existing website does.
          // Without this the row keeps the optimistic local key, and the snippet
          // on screen — the one the customer copies — points at a key that was
          // never created: the iframe renders nothing on their site.
          if (res2?.key) {
            // Same reuse trap as ticking an existing website: re-adding a
            // domain that existed before hands back its old keys untouched,
            // still unpublished. See the note in togglePublish.
            if (res2.reused && res2.published !== true) {
              await updateEmbedKey(String(res2.key), { published: true });
            }
            setPlacements((p) => ({
              ...p,
              [savedId]: {
                ...(p[savedId] || {}),
                [publishFor]: { key: String(res2.key), published: true },
              },
            }));
          }
        }
      },
      "the website",
    );
  };

  /**
   * Delete a website. The ONLY destructive action in the modal — every key
   * bound to it stops rendering, so the customer's pages break. Confirm with
   * the count and point at the reversible alternative.
   */
  const removeSite = (siteId: string) => {
    const site = sites.find((s) => s.id === siteId);
    if (!site) return;
    const n = liveEmbedsOnSite(placements, assets, siteId);
    if (n > 0) {
      const ok = window.confirm(
        `Remove ${site.domain} from this project?\n\n` +
        `${n} embed${n > 1 ? "s" : ""} on that website will stop working, and this can’t be undone.\n\n` +
        `To hide them temporarily instead, untick them or use the switch at the bottom of this window.`,
      );
      if (!ok) return;
    }
    const before = sites;
    const beforeP = placements;
    const nextSites = sites.filter((s) => s.id !== siteId);
    const nextP = { ...placements };
    delete nextP[siteId];
    commit(
      () => { setSites(nextSites); setPlacements(nextP); },
      () => { setSites(before); setPlacements(beforeP); },
      () => deleteEmbedSite(projectId, siteId),
      "that website",
    );
  };

  /**
   * The website's own embed switch.
   *
   * OFF means nothing from this project shows on that domain, whatever the individual ticks
   * say — so turning it off unpublishes every asset there in one go. ON does NOT publish
   * anything: it opens the row up so the customer can choose which assets belong on that site.
   * Turning a website on and having four walkthroughs appear unasked is not a decision anyone
   * made.
   *
   * The ON-with-nothing-chosen state lives only in this component. The server has no
   * per-website embed flag yet — it would be one field on AllowedDomain, alongside
   * `lead_capture`, and is written up for Nivedita — so on reload a website with no published
   * asset reads as off again. Harmless: the ticks are what render, and they persist.
   */
  const siteEmbedsOn = (siteId: string): boolean => {
    // The STORED switch wins where it exists — that is the whole point of the field: the
    // state "on, nothing ticked yet" now survives a reload. Absent, we infer from the ticks,
    // which is how every project that predates the switch keeps behaving.
    const stored = sites.find((s) => s.id === siteId)?.embedsEnabled;
    if (stored === false) return false;
    if (stored === true) return true;
    return liveEmbedsOnSite(placements, assets, siteId) > 0 || !!siteEnabled[siteId];
  };

  /** Persist the switch. Server-side it is what makes render 404 and frame-ancestors empty. */
  const persistSiteEmbeds = (siteId: string, on: boolean) => {
    const before = sites;
    const next = sites.map((s) => (s.id === siteId ? { ...s, embedsEnabled: on } : s));
    commit(
      () => setSites(next),
      () => setSites(before),
      () => updateEmbedSettings(projectId, domainsPayload(next)),
      "that website's embeds",
    );
  };

  const toggleSiteEmbeds = async (s: EmbedSite) => {
    if (!siteEmbedsOn(s.id)) {
      // Just open the row for choosing. Nothing is published until they tick something.
      setSiteEnabled((m) => ({ ...m, [s.id]: true }));
      persistSiteEmbeds(s.id, true);
      return;
    }
    const live = assetsForSite(placements, assets, s.id);
    if (!live.length) {
      setSiteEnabled((m) => ({ ...m, [s.id]: false }));
      persistSiteEmbeds(s.id, false);
      return;
    }
    const ok = window.confirm(
      `Turn off embeds on ${s.domain}?\n\n` +
      `${live.length} ${live.length === 1 ? "embed stops" : "embeds stop"} showing on that ` +
      `website within a minute. The pasted code stays valid, so switching back on needs ` +
      `nothing from their web team.\n\n` +
      `Leads and analytics already collected are kept.`,
    );
    if (!ok) return;

    const before = placements;
    setPlacements((p) => ({
      ...p,
      [s.id]: Object.fromEntries(
        Object.entries(p[s.id] || {}).map(([aid, pl]) => [aid, { ...pl, published: false }]),
      ),
    }));
    setSiteEnabled((m) => ({ ...m, [s.id]: false }));
    persistSiteEmbeds(s.id, false);
    setSave("saving");
    setSaveMsg("");

    // Settled one by one, not Promise.all-or-nothing: a partial failure that reverted the whole
    // row would show embeds back ON while they are genuinely OFF on the customer's website.
    const pairs = live.map((a) => ({ aid: a.id, key: placementAt(before, s.id, a.id)?.key || "" }));
    const results = await Promise.all(pairs.map(async (p) => {
      try {
        await updateEmbedKey(p.key, { published: false });
        return { ...p, ok: true, err: undefined as any };
      } catch (err: any) {
        return { ...p, ok: false, err };
      }
    }));
    const failures = results.filter((r) => !r.ok);
    if (!failures.length) { setSave("idle"); setSaveMsg(""); return; }
    if (failures.every((f) => isNotShipped(f.err))) {
      setSave("unsaved");
      setSaveMsg("Not saved — couldn't reach the server");
      return;
    }
    setPlacements((p) => {
      const fixed: Placements = { ...p, [s.id]: { ...(p[s.id] || {}) } };
      failures.forEach((f) => {
        const prev = before[s.id]?.[f.aid];
        if (prev) fixed[s.id][f.aid] = prev;
      });
      return fixed;
    });
    /* The keys were settled honestly — now the SWITCH has to be. Something survived that is
       still rendering on the customer's website, so a switch reading "off" would be the one
       lie this screen must never tell, and the stored flag takes precedence over the ticks.
       Put it back to on.

       Deliberately NOT through persistSiteEmbeds: that goes via `commit`, which drives the
       save state, and its "saved" would land after the error below and wipe the very message
       that says what failed. Write the flag and fire the PATCH, and let the error stand. */
    setSiteEnabled((m) => ({ ...m, [s.id]: true }));
    const reopened = sites.map((x) => (x.id === s.id ? { ...x, embedsEnabled: true } : x));
    setSites(reopened);
    updateEmbedSettings(projectId, domainsPayload(reopened)).catch(() => {
      /* The switch already reads on locally, which is the honest state. A failure here only
         means it won't survive a reload — and a reload re-infers from the ticks anyway. */
    });
    setSave("error");
    setSaveMsg(bulkOutcome(pairs.length, failures.length, "turn off"));
  };

  /**
   * Revoke a placement left behind by a deleted website. Permanent on purpose — this is the
   * only place in the modal that offers revoke, because it is the only place where the thing
   * being thrown away is already dead and cannot be reached any other way.
   */
  const revokeOrphan = (o: Orphan) => {
    const ok = window.confirm(
      `Revoke this leftover embed permanently?\n\n` +
      `It already shows nothing, because its website was removed. Revoking means re-adding ` +
      `that domain will NOT bring it back. You'd publish it again from scratch.\n\n` +
      `Leads and analytics already collected are kept.`,
    );
    if (!ok) return;
    const before = orphans;
    commit(
      () => setOrphans((list) => list.filter((x) => x.key !== o.key)),
      () => setOrphans(before),
      () => updateEmbedKey(o.key, { revoke: true }),
      "that leftover embed",
    );
  };



  /* ── Derived ───────────────────────────────────────────────────────── */

  const liveEmbeds = countLiveEmbeds(placements, sites, assets);
  const offEmbeds = countOffEmbeds(placements);
  const totalEmbeds = liveEmbeds + offEmbeds;
  /* Websites actually showing something — the number the footer readout needs now that
     "off" is a per-website decision rather than one project-wide flag. */
  const liveWebsites = sites.filter((s) => liveEmbedsOnSite(placements, assets, s.id) > 0).length;
  const liveAssets = countLiveAssets(placements, sites, assets);
  const asset = assets.find((a) => a.id === cur);
  const publishedSites = asset ? sitesForAsset(placements, sites, asset.id) : [];

  const savedClass =
    save === "error" ? " pem-saved--warn"
      : save === "unsaved" ? " pem-saved--pending"
        : "";
  const savedText =
    save === "saving" ? "Saving…"
      : save === "error" ? (saveMsg || "Couldn’t save — reverted")
        : save === "unsaved" ? (saveMsg || "Not saved")
          : "Changes save automatically";

  /* ── Render ────────────────────────────────────────────────────────── */

  /* PORTALLED TO <body> ON PURPOSE.
     ProjectDetailsV3 has transformed ancestors, and a `position: fixed` element
     inside one is positioned against THAT ancestor, not the viewport — which
     pushed the modal's header up out of reach behind the app bar. A portal is
     the only reliable escape; centring maths can't fix a wrong containing
     block. */
  return createPortal(
    <div
      className="pem-root"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="pem-modal" role="dialog" aria-modal="true" aria-labelledby="pem-title">
        <div className="pem-head">
          <span className="pem-head-ico"><Globe /></span>
          <div>
            <div className="pem-head-t" id="pem-title">Publish on your website</div>
            <div className="pem-head-s">
              {projectName}
              {/* The assets arrive as PROPS from the project page, which on a slow connection
                  resolve after this renders — so an unguarded count says "0 assets" about a
                  project that has three. Same lie as the websites one below, one field over. */}
              {assets.length > 0 && (
                <> · <b>{assets.length} asset{assets.length === 1 ? "" : "s"}</b></>
              )}
              {/* The asset count comes from props and is known immediately; the other two
                  come from the server, so they wait rather than guess. */}
              {loaded ? (
                <>
                  {" · "}{sites.length} website{sites.length === 1 ? "" : "s"}
                  {" · "}{liveEmbeds} embed{liveEmbeds === 1 ? "" : "s"} in use
                </>
              ) : (
                <> · loading your websites…</>
              )}
            </div>
          </div>
          <button type="button" className="pem-x" onClick={onClose} aria-label="Close">
            <Close />
          </button>
        </div>

        <div className="pem-body">
          {/* ── Sidebar ── */}
          <div className="pem-side">
            <div className="pem-side-h">
              <p>{projectName}</p>
              <span>{loaded ? `${liveAssets}/${assets.length} live` : "—"}</span>
            </div>
            <button
              type="button"
              className={"pem-xitem" + (cur === "proj" ? " pem-on" : "")}
              data-k="PROJ"
              onClick={() => { setCur("proj"); setOpenRow(null); }}
            >
              <span className="pem-xitem-ico"><TableIco /></span>
              <span>
                <span className="pem-xitem-t">Project-wide settings</span>
                <span className="pem-xitem-s">Matrix · lead gates · domains</span>
              </span>
              <span />
            </button>
            <div className="pem-side-sep" />
            {(["MM", "DT"] as const).map((kind) => {
              const rows = assets.filter((a) => a.kind === kind);
              if (!rows.length) return null;
              return (
                <React.Fragment key={kind}>
                  {/* Meta models are out of scope for this rollout — digital
                      twins only — so the group says so rather than letting a
                      builder publish one and find it behaves differently.
                      A chip, not a banner: the capability is real and coming,
                      and the rows stay usable so any key already minted can
                      still be managed. Drop this when meta models ship. */}
                  <p className="pem-side-grp">
                    {KIND_WORD[kind]}
                    {kind === "MM" && <span className="pem-soon">Next phase</span>}
                  </p>
                  {rows.map((a) => {
                    const n = sitesForAsset(placements, sites, a.id).length;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        className={
                          "pem-xitem" + (a.id === cur ? " pem-on" : "")
                          + (a.kind === "MM" ? " pem-xitem--soon" : "")
                        }
                        data-k={a.kind}
                        title={a.kind === "MM" ? "Meta model embeds arrive in the next phase" : undefined}
                        onClick={() => { setCur(a.id); setOpenRow(null); }}
                      >
                        <span className="pem-xitem-ico">
                          {a.kind === "MM" ? <MMIco /> : <DTIco />}
                        </span>
                        <span>
                          <span className="pem-xitem-t">{a.name}</span>
                          <span className="pem-xitem-s">
                            {a.code}{a.sub ? ` · ${a.sub}` : ""}
                          </span>
                        </span>
                        <span className={"pem-xitem-st" + (n ? "" : " pem-off")}>
                          {assetSiteBadge(n)}
                        </span>
                      </button>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>

          {/* ── Main ── */}
          <div className="pem-main">
            {cur !== "proj" && asset && (
              <>
                <div className="pem-now">
                  <div>
                    <h2 className="pem-now-t">{asset.name}</h2>
                    <p className="pem-now-s">
                      {KIND_WORD[asset.kind]}{asset.sub ? ` · ${asset.sub}` : ""}
                      {/* Said once more where the decision is actually made —
                          the side-list chip is easy to scroll past. */}
                      {asset.kind === "MM" && (
                        <span className="pem-soon">Embeds ship next phase</span>
                      )}
                    </p>
                  </div>
                  <span className="pem-now-code">{asset.code}</span>
                </div>

                <p className="pem-lead">Websites this asset appears on</p>
                <p className="pem-card-p">
                  Tick a website to publish this asset there.{" "}
                  <b>Every website gets its own embed code</b> — paste each one on
                  that site only.
                </p>

                {/* Said plainly here, not just as a chip: this is the screen where
                    someone would otherwise click a tick and wonder why nothing moved. */}
                {asset.kind === "MM" && (
                  <div className="pem-callout pem-callout--soon">
                    <Info />
                    <span>
                      <b>Meta model embeds ship in the next phase.</b> Digital twins are
                      the only asset that can be published to a website right now, so
                      the websites below are locked for this asset. Anything already
                      published here keeps working.
                    </span>
                  </div>
                )}

                <div className="pem-sites">
                  {sites.map((s) => {
                    const on = isPublished(placements, s.id, asset.id);
                    const open =
                      on && (openRow ? openRow === s.id : publishedSites[0]?.id === s.id);
                    const pl = placementAt(placements, s.id, asset.id);
                    // Same two reasons as the matrix, and they read differently: the
                    // website is switched off, or this asset kind isn't in the rollout.
                    // Already-published rows stay unlocked — a live embed has to stay
                    // switchable off, or the only way out is deleting the website.
                    const outOfScope = asset.kind === "MM";
                    const siteOff = !siteEmbedsOn(s.id);
                    const locked = !on && (outOfScope || siteOff);
                    return (
                      <div
                        key={s.id}
                        className={
                          "pem-srow" + (on ? " pem-on" : "") + (open ? " pem-open" : "")
                          + (locked ? " pem-srow--locked" : "")
                        }
                      >
                        <div className="pem-srow-top">
                          <button
                            type="button"
                            className={"pem-cb" + (locked ? " pem-cb--locked" : "")}
                            role="checkbox"
                            aria-checked={on}
                            aria-disabled={locked}
                            disabled={locked}
                            title={
                              outOfScope
                                ? "Meta model embeds arrive in the next phase"
                                : siteOff
                                  ? `Switch embeds on for ${s.domain} in Project-wide settings first`
                                  : undefined
                            }
                            aria-label={`Publish ${asset.code} on ${s.domain}`}
                            onClick={() => togglePublish(s.id, asset.id)}
                          >
                            <Tick />
                          </button>
                          <div>
                            <div className="pem-srow-d">{s.domain}</div>
                            <div className="pem-srow-m">
                              {locked && siteOff && !outOfScope
                                ? "Embeds switched off for this website"
                                : (
                                  <>
                                    {s.lead ? "Lead gate open" : "Lead gate closed"}
                                    {on ? ` · code ${placementLabel(asset, s)}` : ""}
                                  </>
                                )}
                            </div>
                          </div>
                          <div className="pem-srow-act">
                            {/* Chevron only. No edit, no delete — a trash icon here
                                would read as "remove this placement" while deleting
                                the website for the entire project. */}
                            {on && (
                              <button
                                type="button"
                                className="pem-chev"
                                aria-label={open ? "Hide embed code" : "Show embed code"}
                                aria-expanded={open}
                                onClick={() => setOpenRow(open ? "none" : s.id)}
                              >
                                <Chevron />
                              </button>
                            )}
                          </div>
                        </div>
                        {on && pl && (
                          <div className="pem-srow-body">
                            <div className="pem-hosts">
                              <p>Hosts this code will run on</p>
                              <HostList domain={s.domain} subs={s.subs} />
                              <label className="pem-subs">
                                <button
                                  type="button"
                                  className="pem-cb"
                                  role="checkbox"
                                  aria-checked={s.subs}
                                  aria-label={`Include all subdomains of ${s.domain}`}
                                  onClick={(e) => { e.preventDefault(); toggleSubs(s.id); }}
                                >
                                  <Tick />
                                </button>
                                Include all subdomains
                                <em>applies to this website everywhere</em>
                              </label>
                            </div>
                            {/* Only for twins: a meta model is a flat page in an
                                iframe, so "fullscreen" buys it nothing and the
                                kind isn't in this rollout anyway. */}
                            {asset.kind === "DT" && (
                              <div className="pem-openm">
                                <label
                                  className={"pem-openm-row" + (supportsOpenMode ? "" : " pem-openm-row--soon")}
                                >
                                  <button
                                    type="button"
                                    className="pem-sw"
                                    role="switch"
                                    aria-checked={isFs(pl.openMode)}
                                    aria-disabled={!supportsOpenMode}
                                    disabled={!supportsOpenMode}
                                    aria-label={`Fullscreen playing on ${s.domain}`}
                                    onClick={() => setOpenMode(
                                      s.id, asset.id, isFs(pl.openMode) ? "inline" : "fullscreen",
                                    )}
                                  />
                                  <span>
                                    <b>Fullscreen playing</b>
                                    <small>
                                      {isFs(pl.openMode)
                                        ? "Tapping play opens the walkthrough on the visitor’s whole screen"
                                        : "On a computer, tapping play keeps it in the box where you pasted it"}
                                    </small>
                                  </span>
                                </label>
                                {!supportsOpenMode && (
                                  /* Say it, rather than leave a switch that moves and springs
                                     back. This disappears on its own once the server reports
                                     the field — nothing to remember, nothing to redeploy. */
                                  <p className="pem-openm-soon">
                                    <Info />
                                    <span>
                                      Fullscreen is on for every embed, and this switch turns on
                                      with the next platform update. Nothing to do here.
                                    </span>
                                  </p>
                                )}
                                <p className="pem-openm-n">
                                  {/* A recommendation, not a rule — it is their page. */}
                                  <b>We’d leave this on.</b> The whole screen is what makes a
                                  walkthrough feel like a viewing rather than a video on a page.
                                  Turn it off if this sits beside specs, pricing or an enquiry
                                  form and you’d rather not pull the visitor out of that page.
                                  {" "}Either way the ⤢ control stays in the player, so the
                                  visitor can still switch.
                                </p>
                                <p className="pem-openm-n">
                                  {/* Stated, not silently done: a customer who switches this off
                                      and then checks on their phone would otherwise think it broke. */}
                                  <b>Phones always play fullscreen</b>, whichever way this is set —
                                  nothing sits beside a walkthrough on a phone, so in-the-page
                                  would only make it a narrow strip. The exception is{" "}
                                  <b>iPhone</b>, where Safari refuses fullscreen for anything but
                                  video and the walkthrough plays in the page regardless.
                                </p>
                              </div>
                            )}
                            <CodeCard
                              label={placementLabel(asset, s)}
                              domain={s.domain}
                              keyStr={pl.key}
                              assetName={asset.name}
                              projectName={projectName}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* For a meta model this would add the website AND publish to it in one
                      click, which is exactly what this phase does not allow. The website
                      registry itself is still reachable from Project-wide settings. */}
                  {asset.kind !== "MM" && (
                    <div className="pem-addsite">
                      <input
                        className="pem-inp"
                        value={draft}
                        placeholder="add another website — yourdomain.com"
                        onChange={(e) => { setDraft(e.target.value); setDraftErr(""); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); addSite(draft, asset.id); }
                        }}
                      />
                      <button
                        type="button"
                        className="pem-btn pem-btn-line"
                        onClick={() => addSite(draft, asset.id)}
                      >
                        Add &amp; publish here
                      </button>
                    </div>
                  )}
                </div>
                {draftErr && <p className="pem-err">{draftErr}</p>}

                <div className="pem-callout">
                  <Info />
                  <span>
                    {publishedSites.length
                      ? <>On <b>{publishedSites.length} of {sites.length}</b> website
                        {sites.length === 1 ? "" : "s"}. Each has its own code above —
                        paste each one on that website only.</>
                      : <>Not published anywhere yet. Tick a website above to get its
                        embed code.</>}
                  </span>
                </div>
              </>
            )}

            {cur === "proj" && (
              <>
                <div className="pem-now">
                  <div>
                    <h2 className="pem-now-t">Project-wide settings</h2>
                    <p className="pem-now-s">
                      Which asset appears on which website, and whether that website
                      asks for a lead.
                    </p>
                  </div>
                </div>

                <p className="pem-lead">Assets × websites</p>
                <div className="pem-mx">
                  {/* Only the table scrolls sideways. The notes below stay put —
                      see the CSS note: when the whole card was the scroller it
                      swallowed every vertical wheel over it. */}
                  <div className="pem-mxscroll">
                    <table className="pem-mxt">
                      <thead>
                        <tr>
                          <th className="pem-hsite"><span className="pem-mxh">Website</span></th>
                          {/* The website's own switch, beside its lead gate. Two questions about
                              one website: does it show anything, and does it ask for a lead. The
                              asset ticks to the right are detail underneath both. */}
                          <th className="pem-hgate"><span className="pem-mxh">Embeds</span></th>
                          <th className="pem-hgate"><span className="pem-mxh">Lead gate</span></th>
                          {assets.map((a) => (
                            <th key={a.id} className={a.kind === "MM" ? "pem-th--soon" : undefined}>
                              <span className="pem-mxa">{a.code}</span>
                              <span className="pem-mxa-s">{a.name}</span>
                              {a.kind === "MM" && <span className="pem-mxa-n">Next phase</span>}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sites.map((s) => (
                          <tr key={s.id}>
                            <td className="pem-csite">
                              <b>{s.domain}</b>
                              <span>{siteSummary(placements, assets, s)}</span>
                            </td>
                            <td className="pem-cgate">
                              <span className="pem-gatecell">
                                <button
                                  type="button"
                                  className="pem-sw"
                                  role="switch"
                                  aria-checked={siteEmbedsOn(s.id)}
                                  aria-label={`Embeds on ${s.domain}`}
                                  onClick={() => toggleSiteEmbeds(s)}
                                />
                                <small className={siteEmbedsOn(s.id) ? "pem-yes" : undefined}>
                                  {siteEmbedsOn(s.id) ? "On" : "Off"}
                                </small>
                              </span>
                            </td>
                            <td className="pem-cgate">
                              <span className="pem-gatecell">
                                <button
                                  type="button"
                                  className="pem-sw"
                                  role="switch"
                                  aria-checked={s.lead}
                                  aria-label={`Lead gate on ${s.domain}`}
                                  onClick={() => toggleLead(s.id)}
                                />
                                <small className={s.lead ? "pem-yes" : undefined}>
                                  {s.lead ? "Open" : "Closed"}
                                </small>
                              </span>
                            </td>
                            {assets.map((a) => {
                              const on = isPublished(placements, s.id, a.id);
                              // Two reasons a cell can't be ticked, and they read differently:
                              // the website is switched off (turn it on first), or the asset kind
                              // isn't in this rollout at all.
                              const siteOff = !siteEmbedsOn(s.id);
                              const outOfScope = a.kind === "MM";
                              const locked = siteOff || outOfScope;
                              return (
                                <td key={a.id}>
                                  <button
                                    type="button"
                                    className={"pem-cell" + (locked ? " pem-cell--locked" : "")}
                                    role="checkbox"
                                    aria-checked={on}
                                    aria-disabled={locked}
                                    disabled={locked}
                                    title={
                                      outOfScope
                                        ? "Meta model embeds arrive in the next phase"
                                        : siteOff
                                          ? `Switch embeds on for ${s.domain} first`
                                          : undefined
                                    }
                                    aria-label={`${a.code} on ${s.domain}`}
                                    onClick={() => togglePublish(s.id, a.id)}
                                  >
                                    <Tick />
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        {!sites.length && (
                          <tr>
                            <td className="pem-csite" colSpan={assets.length + 3}>
                              <span>No websites yet — add one below to start publishing.</span>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="pem-mxfoot">
                    <Info />
                    <span>
                      <b>Embeds</b> is the master switch for a website. Off means nothing
                      from this project shows there, whatever the ticks say. On does not
                      publish anything by itself — it opens the row so you choose which
                      assets belong on that site. Turning a website back on restores the
                      ticks you had, and the pasted code never changes.
                    </span>
                  </p>
                  <p className="pem-mxfoot">
                    <Info />
                    <span>
                      The <b>lead gate</b> belongs to the website, not the asset. With it
                      open, a visitor is asked once — after they submit on any asset on
                      that site, the other assets on the same site let them straight
                      through. Closed means no gate anywhere on that site.
                    </span>
                  </p>
                  {assets.some((a) => a.kind === "MM") && (
                    <p className="pem-mxfoot">
                      <Info />
                      <span>
                        <b>Meta models ship in the next phase.</b> Only digital twins can
                        be embedded on a website today, so those columns are locked.
                      </span>
                    </p>
                  )}
                </div>

                {orphans.length > 0 && (
                  <div className="pem-sec2">
                    <h3 className="pem-card-t">Left over from deleted websites</h3>
                    <p className="pem-card-p">
                      {orphans.length === 1 ? "This embed was" : "These embeds were"} published on a
                      website you removed, so {orphans.length === 1 ? "it shows" : "they show"} nothing
                      to visitors. Re-adding that domain brings{" "}
                      {orphans.length === 1 ? "it" : "them"} back; revoking is permanent.
                    </p>
                    <div className="pem-chips">
                      {orphans.map((o) => {
                        const a = assets.find((x) => x.id === o.assetId);
                        return (
                          <div key={o.key} className="pem-dom">
                            <div>
                              <b>{a ? a.name : o.label || "Unknown asset"}</b>
                              <span>
                                {o.label ? `code ${o.label} · ` : ""}
                                {/* Name the website. "not showing anywhere" alone left two
                                    leftovers looking like duplicates of each other. */}
                                {o.removedHost
                                  ? `was on ${o.removedHost}, which you removed`
                                  : "not showing anywhere"}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="pem-dx"
                              title={`Revoke this embed permanently`}
                              onClick={() => revokeOrphan(o)}
                            >
                              <Trash />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="pem-sec2">
                  <h3 className="pem-card-t">Websites allowed to show your assets</h3>
                  <p className="pem-card-p">
                    Embeds load <b>only</b> on these domains. Anywhere else shows a short
                    “not authorised” notice, so a copied embed code is useless.
                  </p>
                  <div className="pem-chips">
                    {sites.map((s) => (
                      <div key={s.id} className="pem-dom">
                        <div>
                          <b>{s.domain}</b>
                          <span>
                            {expandHosts(s.domain, s.subs)
                              .filter((h) => h.included)
                              .map((h) => h.host)
                              .join(" · ")}
                          </span>
                        </div>
                        <label className="pem-subs2">
                          <button
                            type="button"
                            className="pem-cb"
                            role="checkbox"
                            aria-checked={s.subs}
                            aria-label={`Include all subdomains of ${s.domain}`}
                            onClick={(e) => { e.preventDefault(); toggleSubs(s.id); }}
                          >
                            <Tick />
                          </button>
                          All subdomains
                        </label>
                        <button
                          type="button"
                          className="pem-dx"
                          aria-label={`Remove ${s.domain} from this project`}
                          title={`Remove ${s.domain} from this project`}
                          onClick={() => removeSite(s.id)}
                        >
                          <Trash />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="pem-addrow">
                    <input
                      className="pem-inp"
                      value={draft}
                      placeholder="yourdomain.com"
                      onChange={(e) => { setDraft(e.target.value); setDraftErr(""); }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); addSite(draft); }
                      }}
                    />
                    <button
                      type="button"
                      className="pem-btn pem-btn-line"
                      onClick={() => addSite(draft)}
                    >
                      Add website
                    </button>
                  </div>
                  {draftErr && <p className="pem-err">{draftErr}</p>}
                  <p className="pem-hint">
                    Domain only — <code>ruchiraprojects.com</code>, not the full page
                    address. <code>www.</code> is added for you.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="pem-foot">
          {/* A READOUT, not a switch. The off-switch moved up into the matrix, one per
              website, because "off" is a decision a customer makes about a particular
              site — a channel partner's page comes down, the main site stays up — and a
              single project-wide switch could not express that. Two controls doing the
              same job, one of them coarser, is how you end up switching off a website you
              never meant to touch. What the footer keeps is the part only it can show:
              where the whole project stands, on one line. */}
          <div className={"pem-allsw" + (totalEmbeds ? "" : " pem-allsw--empty")}>
            <i className={"pem-dot" + (liveEmbeds ? " pem-dot--live" : "")} aria-hidden="true" />
            <span>
              <b>Embeds for {projectName}</b>
              <small>
                {!totalEmbeds
                  ? "Nothing published yet"
                  : liveEmbeds === 0
                    ? `All off. ${offEmbeds} ${offEmbeds === 1 ? "embed" : "embeds"} ready to switch back on`
                    : liveEmbeds === totalEmbeds
                      ? `All ${totalEmbeds} showing on your websites`
                      : `${liveEmbeds} of ${totalEmbeds} showing on your websites`}
                {liveWebsites > 0 && ` · ${liveWebsites} website${liveWebsites === 1 ? "" : "s"}`}
              </small>
            </span>
          </div>
          <div className="pem-foot-right">
            <span className={"pem-saved" + savedClass}>
              {save === "error" ? <Info /> : <Tick />}
              {savedText}
            </span>
            <button type="button" className="pem-btn pem-btn-solid" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PublishEmbedModal;
export { PublishEmbedModal };
